import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Minus, Info } from 'lucide-react';
import { useOffers } from '@/hooks/offers/useOffers';
import { useOfferServices } from '@/hooks/offers/useOfferServices';
import { useIsMobile } from '@/hooks/use-mobile';
import { CATEGORY_MAPPING } from './CategoriesPickStep';
import { Skeleton } from '@/components/ui/skeleton';
interface SelectedOffer {
  id: string;
  title: string;
  category: string;
  quantity: number;
  price: number;
  unitType: string;
  notes?: string;
}
interface CategoryOffersStepProps {
  category: string;
  typeEvenement: string;
  currentSelection: SelectedOffer[];
  onComplete: (data: {
    category: string;
    selectedOffers: SelectedOffer[];
    action: 'next' | 'back';
  }) => void;
}
export const CategoryOffersStep = ({
  category,
  typeEvenement,
  currentSelection,
  onComplete
}: CategoryOffersStepProps) => {
  const {
    data: allOffers = [],
    isLoading
  } = useOffers();
  const [localSelection, setLocalSelection] = useState<SelectedOffer[]>(currentSelection.filter(offer => offer.category === category));

  // Filtrer les offres selon les critères
  const filteredOffers = allOffers.filter(offer => {
    // Convertir la catégorie d'affichage en catégorie de base de données
    const dbCategory = CATEGORY_MAPPING[category];

    // Filtre par catégorie
    if (offer.category !== dbCategory) return false;

    // Pour l'instant, on ne filtre pas par type d'évènement ou gamme
    // car les données n'ont pas ces champs dans la base

    return offer.is_active;
  });
  const OfferPriceDisplay = ({
    offerId,
    offer
  }: {
    offerId: string;
    offer: any;
  }) => {
    const {
      data: services = []
    } = useOfferServices(offerId);
    if (services.length === 0) {
      return <span className="text-muted-foreground">Prix sur devis</span>;
    }

    // Utiliser le prix défini dans offer_services ou le base_price du service
    const pricesWithFallback = services.map(s => {
      const effectivePrice = s.price || s.service?.base_price || 0;
      return {
        ...s,
        effectivePrice,
        price_unit: s.price_unit || s.service?.price_unit || 'par_prestation'
      };
    });
    const validPrices = pricesWithFallback.filter(s => s.effectivePrice > 0);
    if (validPrices.length === 0) {
      return <span className="text-muted-foreground">Prix sur devis</span>;
    }
    const minPrice = Math.min(...validPrices.map(s => s.effectivePrice));
    const service = validPrices.find(s => s.effectivePrice === minPrice);
    const unitText = service?.price_unit === 'par_personne' ? '/ personne' : service?.price_unit === 'par_heure' ? '/ heure' : service?.price_unit === 'par_jour' ? '/ jour' : '';
    return <span className="font-semibold">
        À partir de {minPrice}€ {unitText}
      </span>;
  };
  const AddOfferButton = ({
    offer,
    onAdd
  }: {
    offer: any;
    onAdd: (offerData: any) => void;
  }) => {
    const isMobile = useIsMobile();
    const {
      data: services = []
    } = useOfferServices(offer.id);
    const handleAdd = () => {
      let effectivePrice = 0;
      let priceUnit = 'par_prestation';
      if (services.length > 0) {
        const pricesWithFallback = services.map(s => ({
          price: s.price || s.service?.base_price || 0,
          price_unit: s.price_unit || s.service?.price_unit || 'par_prestation'
        }));
        const validPrices = pricesWithFallback.filter(s => s.price > 0);
        if (validPrices.length > 0) {
          const minPriceService = validPrices.reduce((min, current) => current.price < min.price ? current : min);
          effectivePrice = minPriceService.price;
          priceUnit = minPriceService.price_unit;
        }
      }
      const offerData = {
        ...offer,
        effectivePrice,
        priceUnit
      };
      onAdd(offerData);
    };
    return <Button onClick={handleAdd} size="icon" className="h-8 w-8 shrink-0" aria-label="Ajouter">
        <Plus className="w-4 h-4" />
      </Button>;
  };
  const addOffer = (offerData: any) => {
    const newOffer: SelectedOffer = {
      id: offerData.id,
      title: offerData.title,
      category: category,
      // Utiliser la catégorie d'interface, pas celle de la base
      quantity: offerData.quantifiable ? 1 : 1,
      price: offerData.effectivePrice || 0,
      unitType: offerData.quantifiable ? 'quantité' : offerData.priceUnit === 'par_personne' ? 'par personne' : offerData.priceUnit === 'par_heure' ? 'par heure' : offerData.priceUnit === 'par_jour' ? 'par jour' : 'forfait'
    };
    setLocalSelection(prev => [...prev, newOffer]);
  };
  const updateQuantity = (offerId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      setLocalSelection(prev => prev.filter(offer => offer.id !== offerId));
    } else {
      setLocalSelection(prev => prev.map(offer => offer.id === offerId ? {
        ...offer,
        quantity: newQuantity
      } : offer));
    }
  };
  const removeOffer = (offerId: string) => {
    setLocalSelection(prev => prev.filter(offer => offer.id !== offerId));
  };
  const handleNext = () => {
    onComplete({
      category,
      selectedOffers: localSelection,
      action: 'next'
    });
  };
  const handleBack = () => {
    onComplete({
      category,
      selectedOffers: localSelection,
      action: 'back'
    });
  };
  if (isLoading) {
    return <div className="p-4 space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-xl border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-20" />
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>;
  }
  return <div className="space-y-4 p-3 max-h-[75vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">Offres — {category}</h3>
          
        </div>
        <Badge variant="secondary">{filteredOffers.length} offre{filteredOffers.length > 1 ? 's' : ''}</Badge>
      </div>

      {filteredOffers.length === 0 ? <div className="text-center py-8">
          <Info className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Aucune offre disponible pour cette catégorie dans votre sélection.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Passer à la catégorie suivante ?
          </p>
        </div> : <div className="space-y-3 pr-1">
          {filteredOffers.map(offer => {
        const selectedOffer = localSelection.find(s => s.id === offer.id);
        const isSelected = !!selectedOffer;
        return <Card key={offer.id} className={`transition-all ${isSelected ? 'ring-2 ring-primary' : ''}`}>
                <CardContent className="p-3">
                  <div className="space-y-2">
                    {/* Title and Badge Row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm leading-tight text-foreground break-words">{offer.title}</h4>
                        {offer.quantifiable && (
                          <Badge variant="secondary" className="text-xs mt-1">
                            Quantifiable
                          </Badge>
                        )}
                      </div>
                      {!isSelected && (
                        <div className="shrink-0">
                          <AddOfferButton offer={offer} onAdd={addOffer} />
                        </div>
                      )}
                    </div>

                    {/* Description - Compact */}
                    {offer.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                        {offer.description}
                      </p>
                    )}

                    {/* Price and Selected State Row */}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <div className="flex-1 min-w-0 whitespace-nowrap overflow-hidden">
                        <div className="text-xs font-medium text-muted-foreground truncate">
                          Sur-mesure
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div className="flex items-center gap-1">
                          {offer.quantifiable ? (
                            <div className="flex items-center gap-1">
                              <button 
                                onClick={() => updateQuantity(offer.id, selectedOffer.quantity - 1)} 
                                className="w-10 h-10 md:w-6 md:h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                              >
                                <Minus className="w-4 h-4 md:w-3 md:h-3" />
                              </button>
                              <span className="w-8 md:w-6 text-center font-medium text-sm md:text-xs">{selectedOffer.quantity}</span>
                              <button 
                                onClick={() => updateQuantity(offer.id, selectedOffer.quantity + 1)} 
                                className="w-10 h-10 md:w-6 md:h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                              >
                                <Plus className="w-4 h-4 md:w-3 md:h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-xs px-1.5 py-0.5">Sélectionné</Badge>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => removeOffer(offer.id)} 
                                className="text-xs px-2 py-1 h-6"
                              >
                                Retirer
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>;
      })}
        </div>}

      {/* Navigation */}
      <div className="flex flex-col gap-3 pt-4 border-t">
        {/* Récap des sélections - Mobile optimized */}
        {localSelection.length > 0 && (
          <div className="bg-secondary/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-foreground">
                Récapitulatif
              </span>
              <Badge variant="secondary" className="text-xs font-semibold">
                {localSelection.length} prestation{localSelection.length > 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="space-y-2">
              {localSelection.map(offer => (
                <div key={offer.id} className="bg-background/50 rounded-md p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-foreground truncate">{offer.title}</h5>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          Qté: {offer.quantity}
                        </span>
                        {offer.unitType && offer.unitType !== 'forfait' && (
                          <span className="text-xs text-muted-foreground">
                            ({offer.unitType})
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Tarif retiré côté assistant — précisé dans la proposition sur-mesure */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <p className="text-xs text-center text-muted-foreground">
          Les tarifs vous seront précisés dans votre proposition sur-mesure.
        </p>
        {/* Boutons de navigation */}
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={handleBack} className="text-sm px-3 py-2">
            Revenir
          </Button>
          
          <Button onClick={handleNext} className="text-sm px-3 py-2">
            Suivante
          </Button>
        </div>
      </div>
    </div>;
};